import React, { useEffect, useState, useRef, useCallback } from 'react';
import { SpinnerComponent } from './NewComponents';
import { useSelector } from 'react-redux';
import { getEmailBuilderDetailsByID } from '../../../Services';
import { toast } from 'react-toastify';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';

interface EmailTemplate {
  _id: string;
  emailName: string;
  emailSubject?: string;
  emailPreviewText?: string;
  emailHTML?: string;
  emailJSON?: string;
}

interface EmailDetailsProps {
  item: EmailTemplate | null;
  onValidationChange?: (isValid: boolean) => void;
  onSubjectChange?: (subject: string) => void;
  onPreviewTextChange?: (previewText: string) => void;
  onHtmlDataChange?: (html: string) => void;
}

const EmailDetails: React.FC<EmailDetailsProps> = ({
  item,
  onValidationChange,
  onSubjectChange,
  onPreviewTextChange,
  onHtmlDataChange,
}) => {
  const authtoken = useSelector((state: any) => state.main.auth_token);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [emailData, setEmailData] = useState<EmailTemplate | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [previewText, setPreviewText] = useState<string>('');
  const emailEditorRef = useRef<EditorRef>(null);

  useEffect(() => {
    const isValid = subject.trim() !== '' && previewText.trim() !== '';
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [subject, previewText, onValidationChange]);

  useEffect(() => {
    if (emailData?.emailSubject) {
      setSubject(emailData.emailSubject);
    }
    if (emailData?.emailPreviewText) {
      setPreviewText(emailData.emailPreviewText);
    }
  }, [emailData, onHtmlDataChange]);

  useEffect(() => {
    if (onSubjectChange) onSubjectChange(subject);
    if (onPreviewTextChange) onPreviewTextChange(previewText);
  }, [subject, previewText, onSubjectChange, onPreviewTextChange]);

  useEffect(() => {
    const fetchEmailDetails = async () => {
      if (!item?._id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getEmailBuilderDetailsByID({ _id: item._id }, authtoken);

        if (response?.flag) {
          setEmailData(response.data);
        } else {
          toast.error('Failed to load email details');
        }
      } catch (error) {
        console.error('Error fetching email details:', error);
        toast.error('Error loading email details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmailDetails();
  }, [item, authtoken]);

  const onReady: EmailEditorProps['onReady'] = useCallback(() => {
    if (emailData?.emailJSON) {
      try {
        const templateJson = JSON.parse(emailData.emailJSON);
        emailEditorRef.current?.editor?.loadDesign(templateJson);
        emailEditorRef.current?.editor?.exportHtml((data) => {
          const { html } = data;
          onHtmlDataChange!(html);
        });
      } catch (error) {
        console.error('Error loading email template:', error);
        toast.error('Error loading email template design');
      }
    }
  }, [emailData]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <SpinnerComponent />
      </div>
    );
  }

  return (
    <div className="ann_details_container w-full">
      <div className="ann_details_container__form w-full" style={{ width: '100%' }}>
        <div className="mb-4 w-full" style={{ width: '100%' }}>
          <div className="mb-3">
            <label className="form-label fw-bold">Subject Line</label>
            <input
              type="text"
              className="form-control"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Add Subject Line"
              aria-label="Email subject line"
            />
          </div>

          <div className="mb-4">
            <label className="form-label fw-bold">Preview Text</label>
            <textarea
              className="form-control"
              rows={3}
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Enter preview text that will appear in the email inbox"
              aria-label="Email preview text"
            />
          </div>

          <div className="mb-3" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <label className="form-label fw-bold">Email Content</label>
            <div style={{
              flex: 1,
              height: '500px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              overflow: 'hidden',
            }}>
              <EmailEditor
                ref={emailEditorRef}
                onReady={onReady}
                options={{
                  displayMode: 'email',
                  locale: 'en-US',
                  safeHtml: true,
                  features: {
                    preview: true,
                    imageEditor: true,
                    undoRedo: true,
                  },
                }}
                appearance={{
                  theme: 'light',
                  panels: {
                    tools: {
                      dock: 'left',
                    },
                  },
                }}
                minHeight={500}
                onLoad={onReady}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetails;
